import React, { useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import Table from './LanguagesTable';
import ControlsTree from './ControlsTree';
import Translation from './TranslationPanel';
import listService from 'ListService';
import ToastNotification, { toast } from '../Common/ToastNotification';
import LoadingSpinner from '../Common/LoadingSpinner';
import ExportPanel from './ExportPanel';
import factoryService from 'FactoryService';
import { generateTranslationSchema } from './TranslationUtils';
import { ButtonRow, Button } from '../Common/StyledComponents';
import viewManagerService from 'ViewManagerService';
import undoService from 'UndoService';
import pageSyncService from 'PageSyncService';

const MainContentArea = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1; 
  padding: 12px; /* Padding around the group of panels */
  gap: 20px; /* Space between the panels */
  overflow: hidden; 
  background-color: #ffffff; 
`;

// Left Column: Table + Import/Export
const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 30%; 
  min-width: 250px;
  overflow-y: auto;
`;

const TableSection = styled.div`
  flex-grow: 1; 
`;

const ImportExportSection = styled.div`
  flex-shrink: 0; 
  padding-top: 15px;
`;

// Middle Column: Controls Tree
const MiddlePanel = styled.div`
  flex: 0 0 30%; 
  min-width: 280px;
  overflow-y: auto;  
  border-left: 1px solid #CDCDCD;
`;

// Right Column: Translation Panel
const RightPanel = styled.div`
  flex: 1 1 40%;
  min-width: 300px;
  overflow-y: auto;
`;

// Footer Area: Cancel/Save buttons
const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0; 
  flex-shrink: 0;
  height:68px;
`;

const MultilingualContainer = forwardRef(({ setIsDirty, initialSelectedId, initialSelectedAttribute, requestClose  }, ref) => {

  const pageCollection = pageSyncService.getPageCollection();

  const [selectedNode, setSelectedNode] = useState({});

  const [languages, setLanguages] = useState(() => {
    const allLangs = listService.getAllLanguages();
    allLangs.sort((a, b) => a.name.localeCompare(b.name));
    return JSON.parse(JSON.stringify(allLangs));
  });

  const [currentTranslations, setCurrentTranslations] = useState(() => JSON.parse(JSON.stringify(listService.getAllTranslations())));
  const [isSaving, setIsSaving] = useState(false);

  //Loader state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');

  const handleSetLanguages = useCallback((newLanguages) => {
    const sortAndSet = (langs) => {
      const sorted = [...langs].sort((a, b) => a.name.localeCompare(b.name));
      setLanguages(sorted);
    };

    if (typeof newLanguages === 'function') {
      setLanguages(currentLangs => {
        const updatedLangs = newLanguages(currentLangs);
        updatedLangs.sort((a, b) => a.name.localeCompare(b.name));
        return updatedLangs;
      });
    } else {
      sortAndSet(newLanguages);
    }
  }, []);

  const handleNodeSelectionFromTree = useCallback((selectedNodeId, selectedNodeData) => {
    setSelectedNode(selectedNodeData);
  }, []);

  const handleClose = () => {
    listService.isUndoRedoEnabled = true;
    const currentView = listService.getObject(listService.currentId);

    if (currentView) {
      viewManagerService.renderLayoutPropertyTabs(currentView.model);
      const currentPage = listService.currentPageId;
      //Rerender current page
      pageSyncService.displayPage(currentPage);
    }

    setTimeout(() => { requestClose(); }, 100);
  }

  const handleSave = async (options = {}) => {
    const { keepLoading = false } = options;

    setLoadingText('Saving...');
    setIsLoading(true);

    //Delay so the loading doesn't flicker if the save is quick
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      setIsSaving(true);

      //If the current language is set to a language that was deleted or deactivated set it to default
      const currentLangCode = listService.currentLanguage;
      if (currentLangCode !== 'default') {
        const currentLangObject = languages.find(lang => lang.code === currentLangCode);
        if (!currentLangObject || !currentLangObject.active) {
          listService.currentLanguage = 'default';
        }
      }

      listService.saveLanguages(languages);

      const schema = generateTranslationSchema(listService, factoryService, pageCollection, currentTranslations);
      setCurrentTranslations(schema); // sync with state
      listService.saveTranslations(schema);

      //  delay to show the spinner longer if it's very fast
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsDirty(false);
      toast.show({
        type: 'success',
        title: '',
        message: 'Language configuration saved successfully',
        icon: '',
        position: 'right'
      });

      //This is used just to launch the isDirty function
      undoService.add({ undo: () => { }, redo: () => { } }, "Multilingual");
      setIsSaving(false);
      return true;

    } catch (err) {
      // If any part of the saving process fails
      console.error("Save failed:", err);
      toast.show({
        type: 'error',
        message: `Save failed: ${err.message}`
      });
      setIsSaving(false);
      return false;
    }
    finally {
      if (!keepLoading) {
        setIsLoading(false);
        setLoadingText('');
      }
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  return (
    <>
      <MainContentArea>
        <LeftPanel>
          <TableSection>
            <Table 
              rows={languages} 
              setLanguages={handleSetLanguages} 
              setIsDirty={setIsDirty} 
              currentTranslations={currentTranslations}
              setCurrentTranslations={setCurrentTranslations}/>
          </TableSection>
          <ImportExportSection>
            <ExportPanel onSave={handleSave}
              setLanguages={handleSetLanguages}
              setCurrentTranslations={setCurrentTranslations}
              setIsDirty={setIsDirty}
              setIsLoading={setIsLoading}
              setLoadingText={setLoadingText} />
          </ImportExportSection>
        </LeftPanel>

        <MiddlePanel>
          <ControlsTree
            onNodeSelect={handleNodeSelectionFromTree}
            defaultSelectedId={initialSelectedId}
            defaultSelectedAttribute={initialSelectedAttribute}
            pageCollection={pageCollection}
          />
        </MiddlePanel>

        <RightPanel>
          <Translation selectedObject={selectedNode} languages={languages} currentTranslations={currentTranslations} setCurrentTranslations={setCurrentTranslations} setIsDirty={setIsDirty} />
        </RightPanel>
      </MainContentArea>

      <FooterActions>
        <ButtonRow>
          <Button variant="gray" onClick={handleClose}>Close</Button>
          <Button variant="orange" onClick={handleSave}>Save</Button>
        </ButtonRow>
      </FooterActions>
      <ToastNotification />
      <LoadingSpinner isLoading={isLoading} text={loadingText} type={'radial'} />
    </>
  );
});

export default MultilingualContainer;